import { isAxiosError } from "axios";
import { Button } from "devextreme-react";
import { NumberBox } from "devextreme-react/number-box";
import { Popup } from "devextreme-react/popup";
import { SelectBox } from "devextreme-react/select-box";
import { TextBox } from "devextreme-react/text-box";
import notify from "../../../lib/notify";
import { TFunction } from "i18next";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getProjectsAvailableForUser } from "../../../api/projects";
import axios from "../../../lib/axios";

type ProjectLookupItem = {
  projectId: number;
  projectAcronym: string;
};

const currentDate = new Date();
const currentYear = currentDate.getFullYear();
const currentMonth = currentDate.getMonth() + 1;

type ProjectCommissionHoursV2 = {
  projectId: number;
  projectAcronym: string;
  hours: number;
};

type EmployeeCommissionHoursV2 = {
  employeeId: string;
  employeeFullName: string;
  totalCommissionHours: number | null;
  projects: ProjectCommissionHoursV2[];
};

type EmployeeCommissionHoursV2PatchRequest = {
  companyId: number;
  employeeId: number;
  projectId: number;
  year: number;
  month: number;
  hours: number | null;
};

type CommissionHoursV2PopupProps = {
  onClose: () => void;
};

type PatchHoursFn = (
  employeeKey: string,
  projectId: number,
  projectAcronym: string,
  hours: number | null,
) => Promise<void>;

function extractErrorMessage(error: unknown, t: TFunction): string {
  if (isAxiosError(error)) {
    if (error.response) {
      if (error.response.status === 403) {
        return t("noPermissions");
      }
      if (
        error.response.data &&
        typeof error.response.data === "object" &&
        "message" in error.response.data
      ) {
        return String(error.response.data.message);
      }
    } else if (error.request) {
      return t("common.noResponseFromServer");
    }
  }
  if (error instanceof Error) return error.message;
  return t("common.unknownErrorOccurred");
}

function applyLocalPatch(
  list: EmployeeCommissionHoursV2[],
  employeeKey: string,
  projectId: number,
  projectAcronym: string,
  hours: number | null,
): EmployeeCommissionHoursV2[] {
  return list.map((emp) => {
    if (emp.employeeId !== employeeKey) return emp;

    const existing = emp.projects.find((p) => p.projectId === projectId);
    let nextProjects: ProjectCommissionHoursV2[];
    if (hours == null) {
      nextProjects = emp.projects.filter((p) => p.projectId !== projectId);
    } else if (existing) {
      nextProjects = emp.projects.map((p) =>
        p.projectId === projectId ? { ...p, hours } : p,
      );
    } else {
      nextProjects = [...emp.projects, { projectId, projectAcronym, hours }];
    }

    const total = nextProjects.reduce((sum, p) => sum + p.hours, 0);
    return {
      ...emp,
      projects: nextProjects,
      totalCommissionHours: nextProjects.length === 0 ? null : total,
    };
  });
}

export default function CommissionHoursV2Popup({
  onClose,
}: CommissionHoursV2PopupProps) {
  const { t } = useTranslation();

  const [year, setYear] = useState(() => currentYear);
  const [month, setMonth] = useState(() => currentMonth);
  const [projects, setProjects] = useState<ProjectLookupItem[]>([]);
  const [employees, setEmployees] = useState<EmployeeCommissionHoursV2[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const employeesRef = useRef<EmployeeCommissionHoursV2[]>([]);
  employeesRef.current = employees;
  const reqIdRef = useRef(0);

  const months = useMemo(
    () => [
      { value: 1, label: t("schedule.january") },
      { value: 2, label: t("schedule.february") },
      { value: 3, label: t("schedule.march") },
      { value: 4, label: t("schedule.april") },
      { value: 5, label: t("schedule.may") },
      { value: 6, label: t("schedule.june") },
      { value: 7, label: t("schedule.july") },
      { value: 8, label: t("schedule.august") },
      { value: 9, label: t("schedule.september") },
      { value: 10, label: t("schedule.october") },
      { value: 11, label: t("schedule.november") },
      { value: 12, label: t("schedule.december") },
    ],
    [t],
  );

  useEffect(() => {
    getProjectsAvailableForUser()
      .then((items) =>
        setProjects(
          items.map((p) => ({ projectId: p.id, projectAcronym: p.acronym })),
        ),
      )
      .catch((error) => {
        notify(extractErrorMessage(error, t), "error", 5000);
      });
  }, [t]);

  useEffect(() => {
    const requestId = ++reqIdRef.current;
    setIsLoading(true);
    axios
      .get<EmployeeCommissionHoursV2[]>("/api/v2/employee-commission-hours", {
        params: { year, month },
      })
      .then(({ data }) => {
        if (reqIdRef.current !== requestId) return;
        setEmployees(data);
      })
      .catch((error) => {
        if (reqIdRef.current !== requestId) return;
        notify(extractErrorMessage(error, t), "error", 5000);
        setEmployees([]);
      })
      .finally(() => {
        if (reqIdRef.current === requestId) setIsLoading(false);
      });
  }, [year, month, t]);

  const patchHours = useCallback<PatchHoursFn>(
    async (employeeKey, projectId, projectAcronym, hours) => {
      const [companyId, employeeId] = employeeKey.split(",").map(Number);

      const snapshot = employeesRef.current;
      const optimistic = applyLocalPatch(
        snapshot,
        employeeKey,
        projectId,
        projectAcronym,
        hours,
      );
      setEmployees(optimistic);

      const body: EmployeeCommissionHoursV2PatchRequest = {
        companyId,
        employeeId,
        projectId,
        year,
        month,
        hours,
      };

      try {
        await axios.patch("/api/v2/employee-commission-hours", body);
      } catch (error) {
        setEmployees(snapshot);
        notify(extractErrorMessage(error, t), "error", 5000);
      }
    },
    [year, month, t],
  );

  const handlePreviousMonthButtonClick = useCallback(() => {
    const newValue = month - 1;
    if (newValue < 1) {
      setYear((year) => year - 1);
      setMonth(12);
    } else {
      setMonth(newValue);
    }
  }, [month]);

  const handleNextMonthButtonClick = useCallback(() => {
    const newValue = month + 1;
    if (newValue > 12) {
      setYear((year) => year + 1);
      setMonth(1);
    } else {
      setMonth(newValue);
    }
  }, [month]);

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) =>
      e.employeeFullName.toLowerCase().includes(q),
    );
  }, [employees, search]);

  return (
    <Popup
      visible
      title={t("presence.commissionHoursPopup.title")}
      onHiding={onClose}
      maxWidth={900}
      height="auto"
      maxHeight="80vh"
      showCloseButton
      dragEnabled
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-row flex-wrap items-center justify-between gap-2 border-b border-slate-200 pb-3">
          <TextBox
            value={search}
            onValueChanged={(event) => setSearch(event.value ?? "")}
            placeholder={t("searchPlaceholder")}
            showClearButton
            width={260}
          />
          <div className="flex flex-row items-center gap-2">
            <Button
              icon="chevronprev"
              stylingMode="text"
              onClick={handlePreviousMonthButtonClick}
            />
            <SelectBox
              dataSource={months}
              displayExpr="label"
              valueExpr="value"
              value={month}
              onValueChanged={(event) => setMonth(event.value)}
              width={130}
            />
            <NumberBox
              width={80}
              value={year}
              onValueChanged={(event) => setYear(event.value)}
              showSpinButtons
            />
            <Button
              icon="chevronnext"
              stylingMode="text"
              onClick={handleNextMonthButtonClick}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2 overflow-y-auto pr-1" style={{ maxHeight: "60vh" }}>
          {isLoading ? (
            <div className="py-8 text-center text-sm text-slate-500">
              {t("loading") || "..."}
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">
              {t("noData") || "—"}
            </div>
          ) : (
            filteredEmployees.map((employee) => (
              <EmployeeCard
                key={employee.employeeId}
                employee={employee}
                expanded={expandedId === employee.employeeId}
                onToggle={() =>
                  setExpandedId((id) =>
                    id === employee.employeeId ? null : employee.employeeId,
                  )
                }
                projects={projects}
                patchHours={patchHours}
                t={t}
              />
            ))
          )}
        </div>
      </div>
    </Popup>
  );
}

type EmployeeCardProps = {
  employee: EmployeeCommissionHoursV2;
  expanded: boolean;
  onToggle: () => void;
  projects: ProjectLookupItem[];
  patchHours: PatchHoursFn;
  t: TFunction;
};

const EmployeeCard = memo(function EmployeeCard({
  employee,
  expanded,
  onToggle,
  projects,
  patchHours,
  t,
}: EmployeeCardProps) {
  return (
    <div className="rounded border border-slate-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full flex-row items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50"
      >
        <div className="flex flex-row items-center gap-2">
          <span
            className={`inline-block transition-transform ${
              expanded ? "rotate-90" : ""
            }`}
          >
            ›
          </span>
          <span className="font-semibold text-slate-800">
            {employee.employeeFullName}
          </span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
            {employee.projects.length}
          </span>
        </div>
        <span
          className={
            employee.totalCommissionHours
              ? "font-semibold text-slate-800"
              : "text-slate-400"
          }
        >
          {employee.totalCommissionHours != null
            ? employee.totalCommissionHours.toFixed(2)
            : "—"}
        </span>
      </button>
      {expanded ? (
        <div className="border-t border-slate-200 bg-slate-50 p-4">
          <ProjectsDetail
            employee={employee}
            projects={projects}
            patchHours={patchHours}
            t={t}
          />
        </div>
      ) : null}
    </div>
  );
});

type ProjectsDetailProps = {
  employee: EmployeeCommissionHoursV2;
  projects: ProjectLookupItem[];
  patchHours: PatchHoursFn;
  t: TFunction;
};

const ProjectsDetail = memo(function ProjectsDetail({
  employee,
  projects,
  patchHours,
  t,
}: ProjectsDetailProps) {
  const [newProjectId, setNewProjectId] = useState<number | null>(null);
  const [newHours, setNewHours] = useState<number | null>(null);

  const usedProjectIds = useMemo(
    () => new Set(employee.projects.map((p) => p.projectId)),
    [employee.projects],
  );

  const availableProjects = useMemo(
    () => projects.filter((p) => !usedProjectIds.has(p.projectId)),
    [projects, usedProjectIds],
  );

  const projectAcronymById = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of projects) map.set(p.projectId, p.projectAcronym);
    return map;
  }, [projects]);

  const handleSave = useCallback(
    (projectId: number, hours: number) => {
      const acronym =
        projectAcronymById.get(projectId) ??
        employee.projects.find((p) => p.projectId === projectId)
          ?.projectAcronym ??
        "";
      patchHours(employee.employeeId, projectId, acronym, hours);
    },
    [patchHours, employee.employeeId, employee.projects, projectAcronymById],
  );

  const handleDelete = useCallback(
    (projectId: number) => {
      const acronym =
        projectAcronymById.get(projectId) ??
        employee.projects.find((p) => p.projectId === projectId)
          ?.projectAcronym ??
        "";
      patchHours(employee.employeeId, projectId, acronym, null);
    },
    [patchHours, employee.employeeId, employee.projects, projectAcronymById],
  );

  const handleAdd = useCallback(() => {
    if (!newProjectId || newHours == null || newHours < 0.25) return;
    const acronym = projectAcronymById.get(newProjectId) ?? "";
    patchHours(employee.employeeId, newProjectId, acronym, newHours);
    setNewProjectId(null);
    setNewHours(null);
  }, [
    newProjectId,
    newHours,
    projectAcronymById,
    patchHours,
    employee.employeeId,
  ]);

  return (
    <div className="flex flex-col gap-3">
      {employee.projects.length === 0 ? (
        <div className="text-sm italic text-slate-500">
          {t("presence.commissionHoursPopup.noProjects")}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {employee.projects.map((p) => (
            <ProjectRow
              key={p.projectId}
              project={p}
              onSave={handleSave}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      <div className="flex flex-row flex-wrap items-end gap-2 border-t border-slate-200 pt-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-600">
            {t("presence.commissionHoursPopup.dataGrid.project")}
          </label>
          <SelectBox
            dataSource={availableProjects}
            valueExpr="projectId"
            displayExpr="projectAcronym"
            value={newProjectId}
            onValueChanged={(event) => setNewProjectId(event.value)}
            searchEnabled
            placeholder={t("presence.commissionHoursPopup.selectProject")}
            width={260}
            disabled={availableProjects.length === 0}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-600">
            {t("presence.commissionHoursPopup.hours")}
          </label>
          <NumberBox
            value={newHours ?? undefined}
            onValueChanged={(event) => setNewHours(event.value ?? null)}
            min={0.25}
            max={720}
            step={0.25}
            format="##0.00"
            showSpinButtons
            width={140}
          />
        </div>
        <Button
          icon="add"
          text={t("presence.commissionHoursPopup.addProject")}
          stylingMode="contained"
          type="default"
          onClick={handleAdd}
          disabled={!newProjectId || newHours == null || newHours < 0.25}
        />
      </div>
    </div>
  );
});

type ProjectRowProps = {
  project: ProjectCommissionHoursV2;
  onSave: (projectId: number, hours: number) => void;
  onDelete: (projectId: number) => void;
};

const ProjectRow = memo(function ProjectRow({
  project,
  onSave,
  onDelete,
}: ProjectRowProps) {
  const [hours, setHours] = useState<number | null>(project.hours);
  const lastCommittedRef = useRef(project.hours);

  useEffect(() => {
    setHours(project.hours);
    lastCommittedRef.current = project.hours;
  }, [project.hours]);

  const commit = useCallback(() => {
    if (hours == null || hours < 0.25) return;
    if (hours === lastCommittedRef.current) return;
    lastCommittedRef.current = hours;
    onSave(project.projectId, hours);
  }, [hours, onSave, project.projectId]);

  const handleDelete = useCallback(() => {
    onDelete(project.projectId);
  }, [onDelete, project.projectId]);

  return (
    <div className="flex flex-row items-center gap-3 rounded border border-slate-200 bg-white px-3 py-2">
      <div className="flex-1 font-medium text-slate-800">
        {project.projectAcronym}
      </div>
      <NumberBox
        value={hours ?? undefined}
        onValueChanged={(event) => setHours(event.value ?? null)}
        onFocusOut={commit}
        min={0.25}
        max={720}
        step={0.25}
        format="##0.00"
        showSpinButtons
        width={140}
      />
      <Button
        icon="trash"
        stylingMode="text"
        type="danger"
        onClick={handleDelete}
      />
    </div>
  );
});
